import React, { useState, useEffect, Component } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  LayoutDashboard,
  Plus,
  X,
  RefreshCw,
  Trash2,
  MoreVertical,
  AlertTriangle,
  Clock,
  Users,
  Pencil,
} from "lucide-react";
import { authFetch, apiUrl } from "../../utils/api";
import DashboardWizard from "./DashboardWizard";
import DashboardRenderer from "./DashboardRenderer";
import "./CustomDashboard.css";

class DashboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Dashboard Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="dashboard-error-boundary">
          <AlertTriangle size={48} />
          <h3>Dashboard crashed</h3>
          <p>{this.state.error?.message || "Unknown error"}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const CustomDashboard = ({ onClose }) => {
  const { user } = useUser();
  const userId =
    user?.id ||
    process.env.REACT_APP_DEV_USER_ID ||
    "user_35aUqUMMlEPmKiobX3T3rDS352U";
  const [dashboards, setDashboards] = useState([]);
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  // Refine-chat: Fable edits the CURRENT dashboard in situ (no regenerate)
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);

  const refineDashboardInSitu = async () => {
    const instruction = refineInput.trim();
    if (!instruction || !activeDashboard || refining) return;
    setRefining(true);
    setRefineError(null);
    try {
      const res = await authFetch(
        apiUrl(`/api/custom-dashboards/${activeDashboard.id}/refine`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instruction }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Refine failed");
      const updated = { ...activeDashboard, htmlCode: data.htmlCode, version: data.version };
      setActiveDashboard(updated);
      setDashboards((ds) => ds.map((d) => (d.id === updated.id ? updated : d)));
      setRefineInput("");
    } catch (e) {
      setRefineError(e.message);
    } finally {
      setRefining(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Inline rename (owner-only): pencil next to the header title →
  // PATCH /api/custom-dashboards/:id { name }.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const startRename = () => {
    if (!activeDashboard) return;
    setRenameValue(activeDashboard.name || "");
    setRenaming(true);
  };

  const saveRename = async () => {
    const name = renameValue.trim();
    if (!activeDashboard || renameBusy) return;
    if (!name || name === activeDashboard.name) {
      setRenaming(false);
      return;
    }
    setRenameBusy(true);
    try {
      const res = await authFetch(
        apiUrl(`/api/custom-dashboards/${activeDashboard.id}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Rename failed");
      const updated = { ...activeDashboard, name: data.name };
      setActiveDashboard(updated);
      setDashboards((ds) => ds.map((d) => (d.id === updated.id ? updated : d)));
      setRenaming(false);
    } catch (e) {
      console.error("Error renaming dashboard:", e);
    } finally {
      setRenameBusy(false);
    }
  };

  // Team sharing (flag: dashboard_team_sharing). Owners share a dashboard
  // read-only into one of their teams; teammates see it in their list marked
  // "Shared by <owner>" with refine/refresh/delete hidden.
  const [sharingEnabled, setSharingEnabled] = useState(false);
  const [teams, setTeams] = useState([]);
  const [shareBusy, setShareBusy] = useState(false);
  const isOwner = activeDashboard ? activeDashboard.isOwner !== false : true;

  useEffect(() => {
    if (!sharingEnabled) return;
    (async () => {
      try {
        const res = await authFetch(apiUrl("/api/teams"));
        if (!res.ok) return; // team_workspaces off or no access — no share UI
        const data = await res.json();
        setTeams(data.teams || []);
      } catch (_) {
        /* no share UI without teams */
      }
    })();
  }, [sharingEnabled]);

  const toggleShare = async (teamId) => {
    if (!activeDashboard || shareBusy) return;
    setShareBusy(true);
    try {
      const shared = !!activeDashboard.sharedWithTeam;
      const res = await authFetch(
        apiUrl(
          `/api/custom-dashboards/${activeDashboard.id}/${shared ? "unshare" : "share"}`,
        ),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: shared ? undefined : JSON.stringify({ teamId }),
        },
      );
      if (!res.ok) throw new Error("Share update failed");
      const updated = {
        ...activeDashboard,
        sharedWithTeam: !shared,
        teamId: shared ? null : teamId,
      };
      setActiveDashboard(updated);
      setDashboards((ds) => ds.map((d) => (d.id === updated.id ? updated : d)));
    } catch (e) {
      console.error("Error toggling dashboard share:", e);
    } finally {
      setShareBusy(false);
    }
  };

  // Get user's timezone for display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Display name: strip a redundant "Dashboard" suffix and keep the owner's
  // casing AS TYPED. (This used to title-case every word, which mangled
  // acronyms — "Meta Ads (MOFU)" rendered as "Meta Ads (mofu)" in the picker.)
  const normalizeTitle = (name) => {
    const cleaned = (name || "").replace(/\s+Dashboard$/i, "").trim();
    return cleaned || "Dashboard";
  };

  // Picker labels stay readable even for legacy goal-text names.
  const truncateLabel = (text, max = 48) =>
    text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

  useEffect(() => {
    loadDashboards();
  }, [userId]);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      const response = await authFetch(apiUrl("/api/custom-dashboards"));
      if (!response.ok) throw new Error("Failed to load dashboards");

      const data = await response.json();
      setDashboards(data.dashboards || []);
      setSharingEnabled(!!data.sharingEnabled);

      if (data.dashboards && data.dashboards.length > 0) {
        setActiveDashboard(data.dashboards[0]);
      }
    } catch (error) {
      console.error("Error loading dashboards:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboard = async () => {
    if (!activeDashboard) return;

    try {
      setRefreshing(true);
      const response = await authFetch(
        apiUrl(`/api/custom-dashboards/${activeDashboard.id}/refresh`),
        { method: "POST" },
      );

      if (!response.ok) throw new Error("Failed to refresh dashboard");

      const data = await response.json();
      setActiveDashboard({
        ...activeDashboard,
        lastRefreshedAt: data.timestamp,
      });
    } catch (error) {
      console.error("Error refreshing dashboard:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const deleteDashboard = async (dashboardId) => {
    if (!confirm("Are you sure you want to delete this dashboard?")) return;

    try {
      const response = await authFetch(
        apiUrl(`/api/custom-dashboards/${dashboardId}`),
        { method: "DELETE" },
      );

      if (!response.ok) throw new Error("Failed to delete dashboard");

      setDashboards(dashboards.filter((d) => d.id !== dashboardId));
      if (activeDashboard?.id === dashboardId) {
        setActiveDashboard(
          dashboards.find((d) => d.id !== dashboardId) || null,
        );
      }
    } catch (error) {
      console.error("Error deleting dashboard:", error);
    }
  };

  const handleWizardComplete = async (dashboardConfig) => {
    setShowWizard(false);
    setLoading(true);

    try {
      const response = await authFetch(apiUrl("/api/custom-dashboards"));
      if (!response.ok) throw new Error("Failed to load dashboards");

      const data = await response.json();
      setDashboards(data.dashboards || []);

      if (data.dashboards && data.dashboards.length > 0) {
        const newestDashboard = data.dashboards[0];
        setActiveDashboard(newestDashboard);
      }
    } catch (error) {
      console.error("Error loading dashboards:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatRefreshTime = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading && dashboards.length === 0) {
    return (
      <div className="custom-dashboard-overlay" onClick={onClose}>
        <div className="custom-dashboard" onClick={(e) => e.stopPropagation()}>
          <div className="dashboard-loading">
            <RefreshCw size={48} className="spin" />
            <p>Loading dashboards...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showWizard) {
    return (
      <div className="custom-dashboard-overlay" onClick={onClose}>
        <div
          className="custom-dashboard wizard-mode"
          onClick={(e) => e.stopPropagation()}
        >
          <DashboardWizard
            onComplete={handleWizardComplete}
            onCancel={() => setShowWizard(false)}
            userId={userId}
          />
        </div>
      </div>
    );
  }

  if (dashboards.length === 0) {
    return (
      <div className="custom-dashboard-overlay" onClick={onClose}>
        <div
          className="custom-dashboard empty-state"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="empty-header">
            <button className="close-btn-empty" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="empty-content">
            <LayoutDashboard size={64} className="empty-icon" />
            <h2>Create Your Custom Dashboard</h2>
            <p>Build a dashboard tailored to your business needs.</p>

            <button
              className="btn-create-dashboard"
              onClick={() => setShowWizard(true)}
            >
              <Plus size={20} />
              Create Your First Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-dashboard-overlay" onClick={onClose}>
      <div className="custom-dashboard" onClick={(e) => e.stopPropagation()}>
        <header className="dashboard-header">
          <div className="header-left">
            <img
              src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
              alt="Optimus OS"
              className="header-logo"
              style={{ height: "32px", marginRight: "12px" }}
            />
            <div className="header-title">
              {renaming && isOwner ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename();
                      if (e.key === "Escape") setRenaming(false);
                    }}
                    onBlur={saveRename}
                    maxLength={120}
                    autoFocus
                    disabled={renameBusy}
                    aria-label="Dashboard name"
                    style={{
                      fontSize: 20, fontWeight: 600, padding: "4px 10px",
                      borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)",
                      background: "rgba(255,255,255,0.06)", color: "#fff",
                      minWidth: 260,
                    }}
                  />
                </div>
              ) : (
                <h1 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {normalizeTitle(activeDashboard?.name) || "Custom Dashboard"}
                  {isOwner && activeDashboard && (
                    <button
                      className="btn-icon"
                      onClick={startRename}
                      title="Rename dashboard"
                      aria-label="Rename dashboard"
                      style={{ opacity: 0.6 }}
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                </h1>
              )}
              <p className="header-subtitle">
                Last updated:{" "}
                {formatRefreshTime(activeDashboard?.lastRefreshedAt)}
              </p>
            </div>
          </div>

          <div className="header-actions">
            <span className="timezone-indicator">
              <Clock size={16} />
              {userTimezone.split("/")[1] || userTimezone}
            </span>
            {isOwner && (
              <button
                className="btn-icon"
                onClick={refreshDashboard}
                disabled={refreshing}
                title="Refresh Dashboard"
              >
                <RefreshCw size={20} className={refreshing ? "spin" : ""} />
              </button>
            )}
            <button className="btn-icon" onClick={onClose} title="Close">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="dashboard-tabs">
          <div className="tabs-left-group">
            <select
              className="dashboard-selector"
              value={activeDashboard?.id || ""}
              onChange={(e) => {
                const selected = dashboards.find(
                  (d) => d.id === e.target.value,
                );
                setRenaming(false);
                setActiveDashboard(selected);
              }}
            >
              {dashboards.map((dash) => (
                <option key={dash.id} value={dash.id}>
                  {truncateLabel(normalizeTitle(dash.name))}
                  {dash.isOwner === false
                    ? ` — shared by ${(dash.ownerEmail || "a teammate").split("@")[0]}`
                    : dash.sharedWithTeam
                      ? " — shared with team"
                      : ""}
                </option>
              ))}
            </select>

            <button
              className="btn-new-dashboard"
              onClick={() => setShowWizard(true)}
            >
              <Plus size={18} />
              New Dashboard
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!isOwner && activeDashboard && (
              <span
                className="dashboard-shared-badge"
                title="This dashboard was shared with your team — view only"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontSize: 13, color: "rgba(255,255,255,0.6)",
                }}
              >
                <Users size={14} />
                Shared by {(activeDashboard.ownerEmail || "a teammate").split("@")[0]} · view only
              </span>
            )}
            {isOwner && sharingEnabled && teams.length > 0 && activeDashboard && (
              activeDashboard.sharedWithTeam ? (
                <button
                  className="btn-new-dashboard"
                  onClick={() => toggleShare(null)}
                  disabled={shareBusy}
                  title="Stop sharing with your team"
                >
                  <Users size={16} />
                  Unshare
                </button>
              ) : teams.length === 1 ? (
                <button
                  className="btn-new-dashboard"
                  onClick={() => toggleShare(teams[0].id)}
                  disabled={shareBusy}
                  title={`Share read-only with ${teams[0].name}`}
                >
                  <Users size={16} />
                  Share with team
                </button>
              ) : (
                <select
                  className="dashboard-selector"
                  value=""
                  disabled={shareBusy}
                  onChange={(e) => e.target.value && toggleShare(e.target.value)}
                  title="Share read-only with one of your teams"
                >
                  <option value="">Share with team…</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )
            )}
            {isOwner && (
              <button
                className="btn-delete-dashboard"
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete "${normalizeTitle(activeDashboard?.name)}"?`,
                    )
                  ) {
                    deleteDashboard(activeDashboard.id);
                  }
                }}
                title="Delete Dashboard"
              >
                <Trash2 size={16} />
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="dashboard-content">
          {activeDashboard && (
            <DashboardErrorBoundary>
              <DashboardRenderer
                dashboard={activeDashboard}
                userId={userId}
                readOnly={!isOwner}
                onRefresh={refreshDashboard}
                // Refine is owner-only (server enforces; don't render dead
                // "Ask Fable to fix" affordances for read-only team viewers).
                onAskFable={isOwner ? (text) => setRefineInput(text) : undefined}
              />
            </DashboardErrorBoundary>
          )}
        </div>

        {activeDashboard && isOwner && (
          <div
            className="dashboard-refine-bar"
            style={{
              display: "flex", gap: 8, alignItems: "center",
              padding: "10px 16px", borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <input
              type="text"
              value={refineInput}
              onChange={(e) => setRefineInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && refineDashboardInSitu()}
              placeholder="Ask Fable to tweak this dashboard… (e.g. 'add a revenue trend line', 'make the KPI cards bigger')"
              disabled={refining}
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.06)", color: "#fff",
              }}
            />
            <button
              onClick={refineDashboardInSitu}
              disabled={refining || !refineInput.trim()}
              style={{
                padding: "10px 18px", borderRadius: 8, border: "none",
                background: refining ? "#555" : "#00d4ff", color: "#0b0e14",
                fontWeight: 600, cursor: refining ? "wait" : "pointer",
              }}
            >
              {refining ? "Refining…" : "Refine"}
            </button>
            {refineError && (
              <span style={{ color: "#ff7b7b", fontSize: 13 }}>{refineError}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDashboard;
